'use client';

/**
 * Scene asset loading — manifest + images + alpha occupancy grids.
 *
 * The manifest (the catalog's scene manifest, which the host hands over)
 * holds each symbol's art by role, symbol_id → { backdrop?, mid? }: url, pixel
 * size, painted bbox (image-normalized), zone/class, and for a backdrop a
 * ridge profile and named region polygons. An entry also lists its active
 * pieces by style (TT-1310); the styles a host asks for pick one
 * (lib/artPick.js), with its own metadata. This module adds the
 * browser-only part: decoding the image and rasterizing its alpha into a
 * coarse grid the solver uses for silhouette-aware collision.
 */

import { templateArt } from './sceneTemplates.js';
import { pickArtPiece, sceneArt, stylesKey } from '../lib/artPick.js';
import { useEffect, useState } from 'react';

const ALPHA_COLS = 64;
const ALPHA_THRESHOLD = 40; // 0-255; below = "not painted"

const manifestCache = new Map();
const imageCache = new Map();

// A manifest from its URL, fetched once per URL, or one the host already
// holds: a host that reads the art from the catalog itself (tapp, through its
// own appback client) hands the renderer the object. None: no art.
export function loadManifest(source) {
  if (!source) return Promise.resolve({ assets: {} });
  if (typeof source === 'object') return Promise.resolve(source);
  const url = source;
  if (!manifestCache.has(url)) {
    manifestCache.set(
      url,
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`manifest ${r.status}`);
        return r.json();
      })
    );
  }
  return manifestCache.get(url);
}

function decodeImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image failed: ${url}`));
    img.src = url;
  });
}

function loadImage(url) {
  if (!imageCache.has(url)) {
    const loaded = decodeImage(url)
      .catch(async () => {
        // Other canvases display this art without CORS. Use a separate
        // cache key for pixel reads so their cached response cannot block
        // the alpha grid; retry the same artwork once.
        const readable = new URL(url, document.baseURI);
        readable.searchParams.set('scene_pixels', '1');
        const response = await fetch(readable.href, { mode: 'cors', cache: 'reload' });
        if (!response.ok) throw new Error(`image ${response.status}: ${url}`);
        const local = URL.createObjectURL(await response.blob());
        try {
          return await decodeImage(local);
        } finally {
          URL.revokeObjectURL(local);
        }
      })
      .catch((error) => {
        imageCache.delete(url);
        throw error;
      });
    imageCache.set(url, loaded);
  }
  return imageCache.get(url);
}

/**
 * Rasterize the image's alpha channel into a {cols, rows, data} grid of 0/1.
 * Each cell is 1 if any sampled pixel in it is painted.
 */
const DENSE_THRESHOLD = 150; // alpha of 255: painted at all
// The seed art is opaque wherever it is painted (alpha ≈ 0 or 255), so
// "dense" is decided by luminance: only the darker body/shadows of the
// watercolor block text; the light wash (lum ≥ DENSE_LUM) is soft.
const DENSE_LUM = 212;

export function alphaGrid(img, cols = ALPHA_COLS) {
  const rows = Math.max(1, Math.round((cols * img.naturalHeight) / img.naturalWidth));
  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  // Drawing downscaled averages alpha per cell; the threshold then decides.
  ctx.drawImage(img, 0, 0, cols, rows);
  const px = ctx.getImageData(0, 0, cols, rows).data;
  const data = new Uint8Array(cols * rows);
  // `dense`: solid paint (a hard obstacle for text); `data`: any paint at
  // all (pale wash included — free-with-a-cost for art-first scenes).
  const dense = new Uint8Array(cols * rows);
  for (let i = 0; i < cols * rows; i++) {
    const a = px[i * 4 + 3];
    const lum = 0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2];
    data[i] = a >= ALPHA_THRESHOLD ? 1 : 0;
    dense[i] = a >= DENSE_THRESHOLD && lum < DENSE_LUM ? 1 : 0;
  }
  return { cols, rows, data, dense };
}

/**
 * Load the art a spec draws (specArt: { symbolId, role } pairs). Returns the
 * manifest's shape, symbol_id → role → { ...manifestEntry, reveal, img, alpha }
 * (read with sceneArt), the entry being the piece in one of `styles`
 * (style names) if it has one, else the default.
 */
export async function loadSceneAssets(art, manifestSource, styles = null) {
  const manifest = await loadManifest(manifestSource);
  const out = {};
  await Promise.all(
    art.map(async ({ symbolId, role }) => {
      const entry = pickArtPiece(sceneArt(manifest.assets, symbolId, role), styles);
      if (!entry) {
        console.warn('[scene] no art for', symbolId, role);
        return;
      }
      // The piece's intro, one descriptor (StagedArt): a clip wins over
      // stage frames. Only stage frames need warming (the flipbook must
      // never stall on a fetch; a missing frame drops the intro, not the art).
      const wantStages = !entry.intro ? entry.stages || [] : [];
      const [img, stages] = await Promise.all([
        loadImage(entry.url),
        Promise.all(wantStages.map((u) => loadImage(u).then(() => u, () => null))),
      ]);
      const reveal = entry.intro
        ? { kind: 'clip', src: entry.intro }
        : stages.length && stages.every(Boolean)
          ? { kind: 'stages', urls: stages }
          : null;
      (out[symbolId] ||= {})[role] = { ...entry, reveal, img, alpha: alphaGrid(img) };
    })
  );
  return out;
}

/**
 * The art a spec draws, as { symbolId, role }. Story boards compose around
 * their metaphor's template art (sceneTemplates.js); other scenes name their
 * own: the anchor is a backdrop, each element a mid.
 */
export function specArt(spec) {
  if (spec?.archetype === 'story') return templateArt(spec.metaphor);
  const art = [];
  if (spec?.anchor?.symbol_id) art.push({ symbolId: spec.anchor.symbol_id, role: 'backdrop' });
  for (const e of spec?.elements || []) if (e.symbol_id) art.push({ symbolId: e.symbol_id, role: 'mid' });
  return art;
}

// A spec's art as one stable key ("backdrop:mountain|mid:sun"), and back:
// hosts hand over a fresh spec object every poll.
const artKey = (art) => [...new Set(art.map(({ symbolId, role }) => `${role}:${symbolId}`))].sort().join('|');
const artOfKey = (key) =>
  key.split('|').map((k) => {
    const at = k.indexOf(':');
    return { role: k.slice(0, at), symbolId: k.slice(at + 1) };
  });

// Nothing to load (e.g. a word wall): one stable object, so hosts keyed on
// `assets` don't re-solve every render.
const NO_ASSETS = { key: '', assets: {}, error: null };

/**
 * React hook: assets for a spec, or null until loaded. `manifest` is a URL
 * or a manifest object (loadManifest); null while the host is still loading
 * it, which holds the assets back. `styles`: the styles to draw the art in
 * (loadSceneAssets); a change loads the pieces they pick.
 */
export function useSceneAssets(spec, manifest, styles = null) {
  const art = artKey(specArt(spec));
  const asked = stylesKey(styles);
  const key = art && `${art}#${asked}`;
  const [state, setState] = useState({ key: null, assets: null, error: null });
  useEffect(() => {
    if (!art || manifest === null) return undefined;
    let cancelled = false;
    loadSceneAssets(artOfKey(art), manifest, asked ? asked.split(',') : null)
      .then((assets) => !cancelled && setState({ key, assets, error: null }))
      .catch((error) => !cancelled && setState({ key, assets: null, error }));
    return () => {
      cancelled = true;
    };
  }, [key, art, asked, manifest]);
  if (!key) return NO_ASSETS;
  return state.key === key ? state : { key, assets: null, error: null };
}

/**
 * A JSON manifest (`loadManifest`: a URL, or an object the host loaded) as
 * state: `pick` of it, `{}` when it is missing, null until known. A null
 * source is a host still loading it.
 */
const whole = (m) => m;
export function useManifest(source, pick = whole) {
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (source === null) return undefined;
    let cancelled = false;
    loadManifest(source)
      .then(pick)
      .catch(() => ({}))
      .then((m) => !cancelled && setMap(m));
    return () => {
      cancelled = true;
    };
  }, [source, pick]);
  return map;
}

// A manifest's assets map: the board art and the text-box art are both shaped { assets }.
export const pickAssets = (m) => m.assets || {};

/**
 * The painted text boxes (TT-1269): mood → piece from
 * `/text-box-art/manifest.json`, or an empty map when a host ships none
 * (every motif has a drawn fallback); null until known. The board's and the
 * scene's canvases both wait on it (a mooded box's reach comes from it).
 */
export function useTextBoxArt(url = '/text-box-art/manifest.json') {
  return useManifest(url, pickAssets);
}
